<template>
  <div
    class="group relative grow flex items-center h-full cursor-pointer"
    @wheel="onWheel"
    @click="seek"
    @mousedown="dragStart"
    @touchstart="touchStart"
    @mousemove="hover"
    @touchmove="hover"
    @mouseover="showPopup"
    @mouseout="hidePopup"
    @touchend="hidePopup"
  >
    <div
      ref="slider"
      class="flex justify-start h-px w-full bg-player-control-default group-hover:bg-player-control-darkened transition-colors duration-200"
    >
      <div
        class="relative h-full bg-player-control-lightened duration-200"
        :class="[
          { 'bg-opacity-25': muted },
          // Transition `width` only on click, but not while dragging.
          // A `width` transition while dragging creates an undesirable lagging effect.
          dragging ? 'transition-colors' : 'transition-all',
        ]"
        :style="{ width: `${progress}%` }"
      >
        <transition-fade :duration="500">
          <span
            v-if="currentTime > 0"
            class="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/2 block w-4 h-4 bg-player-control-default rounded-full"
          />
        </transition-fade>
      </div>
    </div>
    <transition-fade :duration="300">
      <div
        v-show="popupVisible"
        class="absolute bg-black text-white rounded-md p-1 -translate-x-1/2 -translate-y-full"
        :style="{
          left: `${popupPositionX}%`,
        }"
      >
        {{ popupLabel }}
      </div>
    </transition-fade>
  </div>
</template>

<script>
// TODO: Consider refactoring ProgressControl & VolumeControl to use a common Slider,
//       with orientation (horizontal / vertical) option.

import TransitionFade from "../../../../magic/TransitionFade.vue";

const timeFormatter = new Intl.DateTimeFormat("nl-NL", {
  minute: "2-digit",
  second: "2-digit",
});

export default {
  name: "ProgressControl",
  components: {
    TransitionFade,
  },
  data() {
    return {
      dragging: false,
      popupLabel: "Laden...",
      popupPositionX: 0,
      popupInTransition: false,

      popupVisible: false,
      closeDelay: 50,
      closeTimeout: null,
    };
  },
  props: {
    duration: {
      type: Number, // `undefined` while the song has not finished loading!
    },
    currentTime: {
      type: Number,
    },
    muted: {
      type: Boolean,
    },
  },
  computed: {
    progress() {
      if (!this.duration) {
        return 0;
      }
      return (this.currentTime / this.duration) * 100;
    },
  },
  methods: {
    onWheel(event) {
      if (!this.duration) {
        return;
      }
      // Change volume by mouse wheel while hovering the volume control.
      // Clamp the volume to values from 0 to song duration.
      const currentTime = Math.max(
        0,
        Math.min(this.currentTime + event.deltaY / 10, this.duration),
      );
      this.$emit("update:currentTime", currentTime);
    },
    seek(event) {
      this.$emit("update:currentTime", this.getTime(event));
    },
    dragStart() {
      // Start mouse down + move dragging to freely adjust the playback progress.
      this.dragging = true;
      document.addEventListener("mousemove", this.drag);
      document.addEventListener("mouseup", this.dragEnd, { once: true });
    },
    touchStart(event) {
      if (event.touches.length > 1) {
        return;
      }

      // Start touch + move dragging to freely adjust the playback progress.
      this.dragging = true;
      this.showPopup();

      document.addEventListener("touchmove", this.drag);
      document.addEventListener("touchend", this.dragEnd, { once: true });
    },
    drag(event) {
      // Use the mouse move event to set the playback progress,
      // if we are currently dragging (= after mouse down).
      if (!this.dragging) {
        return;
      }
      this.seek(event);
    },
    dragEnd() {
      // End dragging with the mouse up event.
      // Cancel the mouse move listener and stop drag-mode.
      document.removeEventListener("mousemove", this.drag);
      document.removeEventListener("touchmove", this.drag);
      this.dragging = false;
    },
    hover(event) {
      if (!this.duration) {
        // Song has not finished loading.
        // Popup position and label are as initially defined in `data()`.
        return;
      }
      const time = this.getTime(event);
      this.popupPositionX = (time / this.duration) * 100;
      this.popupLabel = timeFormatter.format(new Date(time * 1000));
    },
    getTime(event) {
      if (!this.duration) {
        return 0;
      }
      const clientX =
        event.type === "touchmove" ? event.touches[0].clientX : event.clientX;
      // Set the progress via a click or mouse move (if currently dragging) event,
      // by inspecting the event's coordinates in respect to the slider's bounding rect.
      const boundingBox = this.$refs.slider.getBoundingClientRect();
      let progressFactor = (clientX - boundingBox.x) / boundingBox.width;
      // Clamp the progress to values from 0 to almost 1.
      // Why almost 1? Because setting an audio element's currentTime
      // to its duration makes it jump to the start. We want to stay at the end.
      progressFactor = Math.max(0, Math.min(progressFactor, 0.99));
      // Map the calculated progress onto an absolute time.
      return this.duration * progressFactor;
    },
    showPopup() {
      // Show the popup. If it is currently waiting to close, cancel the wait.
      if (this.closeTimeout) {
        window.clearTimeout(this.closeTimeout);
      }
      this.popupVisible = true;
    },
    hidePopup() {
      // Hide the popup after waiting for a moment,
      // for improved UX and flicker-free display.
      const vm = this;
      this.closeTimeout = window.setTimeout(() => {
        vm.popupVisible = false;
      }, this.closeDelay);
    },
  },
};
</script>
